import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { useNavigate } from "@tanstack/react-router";
import { NotificationActions } from "@/lib/notification-actions-plugin";

// Botão "Ver mapa" da notificação nativa de alerta de vibração
// (2026-09-14, ver GpsDriveMessagingService.java no projeto
// android-capacitor). O app carrega o site de produção de verdade dentro
// da WebView (ver capacitor.config.ts) — não existe deep link/URL scheme
// real, então o único jeito de o React saber que o usuário tocou o botão
// é perguntar pro nativo (NotificationActionsPlugin#getPending) assim que
// o bridge montar e de novo toda vez que o app volta de segundo plano:
// como MainActivity é singleTask, tocar "Ver mapa" com o app já rodando
// só reaproveita a Activity existente (dispara o evento 'resume' do
// Capacitor, nunca um novo mount do React).
//
// "Dispensar alarme" NÃO passa por aqui — roda 100% nativo
// (SnoozeAlarmReceiver.java, sem abrir o app) porque só precisa do cookie
// de sessão já persistido no WebView, não de nenhuma navegação.
export function useNativeNotificationActions(enabled: boolean): void {
  const navigate = useNavigate();
  const checkingRef = useRef(false);

  useEffect(() => {
    if (!enabled || !Capacitor.isNativePlatform()) return;

    const check = () => {
      if (checkingRef.current) return;
      checkingRef.current = true;
      NotificationActions.getPending()
        .then((pending) => {
          if (pending.action === "open_map" && pending.deviceId) {
            void navigate({ to: "/", search: { vehicle: pending.deviceId } });
          }
        })
        .catch(() => {
          // Plugin só existe no app nativo empacotado — uma versão mais
          // antiga do APK sem o plugin ainda instalado simplesmente nunca
          // encontra nada pra fazer aqui, não é um erro real.
        })
        .finally(() => {
          checkingRef.current = false;
        });
    };

    check();
    const listenerPromise = App.addListener("resume", check);
    return () => {
      void listenerPromise.then((listener) => listener.remove());
    };
  }, [enabled, navigate]);
}
